import * as Page from '../../components/content/page-header';
import * as Button from '../../components/button/button';
import { Link } from 'react-router-dom';
import { Icons } from '../../components/icons/icons';
import { useNavigate, useLocation } from 'react-router';
import { PaginatedTable } from '../../components/table/paginated-table';
import { MainContent } from '../../components/layout/content';
import usePageState from '../../hooks/use-page-state';
import { Modes } from '../../types/page-modes';
import { Actions, Content, Dialog, Title } from '../../components/dialog/dialog';
import React, { useMemo, useState } from 'react';
import { Query, usePagination } from '../../hooks/use-pagination';
import {
  ActionColumn,
  CellRenderer,
  GearIconColumn,
  ManuallyAddedColumn
} from '../../components/table/columns';
import { useDeleteMutation } from '../../hooks/use-mutations';
import { MapServer } from './types/map-server';
import { Pageable } from '../../services/base-service';
import { Protocol } from '../settings/types/protocol';
import { KeyValue } from '../../types/key-value';
import { NotInSync } from '../../components/not-in-sync/not-in-sync';
import { RevertToMaster2 } from '../../components/revert-to-master/revert-to-master-2';
import { SearchField, useSearch } from '../../hooks/use-search';

export const MapServers = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState<Query>({});
  const { data, isLoading } = usePagination('/map-servers', query);
  const deleteMutation = useDeleteMutation();
  const { isSearching, searchData, setSearchQuery } = useSearch<MapServer>({ path: '/map-servers/search' });
  const protocol = usePagination<Pageable<Protocol>>('/protocols', { size: 9999 });
  const protocolData = protocol.data?.content || [];
  const protocolMap = useMemo(() => {
    const map: KeyValue = {};
    protocolData?.forEach((item): void => {
      map[item.id] = item.name;
    });
    return map;
  }, [protocolData]);

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/map-servers/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const columns = [
    ...GearIconColumn(),
    { accessor: 'name', Header: 'Name', sort: true, Cell: CellRenderer },
    { accessor: 'protocolId', Header: 'Protocol', Cell: (item: any): JSX.Element => {
      const row = item.row.original as MapServer;
      return <span>{protocolMap[row.protocolId]}</span>;
    } },
    { accessor: 'protocolVersion', Header: 'Protocol Version', Cell: CellRenderer },
    { accessor: 'software', Header: 'Software', Cell: CellRenderer },
    { accessor: 'countServices', Header: 'Map Services', Cell: (item: any): JSX.Element => {
      const row = item.row.original as MapServer;
      return <Link to={`/map-services?mapServerId=${row.id}`} className="text-decoration-none">{row.countServices}</Link>;
    } },
    ManuallyAddedColumn(),
    ActionColumn({
      onDelete: (item: MapServer): void => setPageState(Modes.DELETE, item),
      onUpdate: (item: MapServer): void => navigate(`/map-servers/${item.id}`)
    })
  ];

  return <>
    <Page.Header>
      <Page.Tenant />
      <Page.Title>Map Servers <NotInSync path="map-servers" /></Page.Title>
      <Page.SubTitle>PDC hosted map servers as well as external map servers.</Page.SubTitle>
      <Page.Actions>
        <SearchField />
        <div>
          <RevertToMaster2 path="map-servers" />
          <Button.PrimarySmall onClick={(): void => navigate(`${pathname}/new`)}>
            <Icons.Add />
            Add Map Server
          </Button.PrimarySmall>
        </div>
      </Page.Actions>
    </Page.Header>
    <MainContent>
      {isSearching
        ? <PaginatedTable isLoading={isLoading} columns={columns} data={searchData} onChange={setSearchQuery} />
        : <PaginatedTable isLoading={isLoading} columns={columns} data={data} onChange={setQuery} />
      }
    </MainContent>
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Map Server</Title>
        <Content>Are you sure you want to delete?</Content>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};
